import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import ModifierGroupDialog from "~/components/modifier-group/modifier-group-dialog";
import ModifierDialog from "~/components/modifier/modifier-dialog";
import ProductCategoryDialog from "~/components/product-category/product-category-dialog";
import ProductDialog from "~/components/product/product-dialog";
import { ProductContext } from "~/hooks/use-product";
import { api } from "~/lib/api";
import { useLoadingStore } from "~/stores/use-loading-store";
import { UnsavedChangesProvider } from "./unsaved-changes-provider";

export type Product = RouterOutputs["product"]["getAll"][number];

export type ProductModifier =
  RouterOutputs["productModifier"]["getAll"][number];

export type ProductCategory =
  RouterOutputs["productCategory"]["getAll"][number];

export type ProductModifierGroup =
  RouterOutputs["productModifierGroup"]["getAll"][number];

interface ProductProviderProps {
  children: ReactNode;
}

export function ProductProvider({ children }: ProductProviderProps) {
  const { data: products, isFetching: isProductsLoading } =
    api.product.getAll.useQuery(undefined, {
      initialData: [],
    });

  const { data: modifiers } = api.productModifier.getAll.useQuery(undefined, {
    initialData: [],
  });

  const { data: productCategories } = api.productCategory.getAll.useQuery(
    undefined,
    {
      initialData: [],
    },
  );

  const { data: modifierGroups } = api.productModifierGroup.getAll.useQuery(
    undefined,
    {
      initialData: [],
    },
  );

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  useEffect(() => {
    if (isProductsLoading) {
      setLoadingStage("products");
    } else {
      setLoadingStage(undefined);
    }
  }, [isProductsLoading, setLoadingStage]);

  const context = {
    products,
    modifiers,
    modifierGroups,
    productCategories,
  };

  return (
    <ProductContext.Provider value={context}>
      {children}
      <UnsavedChangesProvider>
        <ProductDialog />
      </UnsavedChangesProvider>
      <UnsavedChangesProvider>
        <ProductCategoryDialog />
      </UnsavedChangesProvider>
      <UnsavedChangesProvider>
        <ModifierGroupDialog />
      </UnsavedChangesProvider>
      <UnsavedChangesProvider>
        <ModifierDialog />
      </UnsavedChangesProvider>
    </ProductContext.Provider>
  );
}
